import { z } from 'zod';
import { ORG_ROLES, PLATFORM_CAPABILITIES } from "./authorization.js";
/**
 * Request and response contracts (SDAD Section 10).
 *
 * These live in a shared package rather than in the API because the browser
 * journey tests must exercise the same contract the product does. A test that
 * talks to its own idea of the API proves nothing about the product
 * (kickoff Section 14).
 */
export const MIN_PASSWORD_LENGTH = 12;
export const MAX_PASSWORD_LENGTH = 256;
export const passwordSchema = z
    .string()
    .min(MIN_PASSWORD_LENGTH, `Use at least ${MIN_PASSWORD_LENGTH} characters.`)
    .max(MAX_PASSWORD_LENGTH);
export const emailSchema = z.string().trim().toLowerCase().email().max(320);
/**
 * Phone input is accepted as typed and normalised to E.164 server-side with an
 * explicit country context (SDAD Section 12.1). The contract therefore carries
 * the raw string plus the region, never a half-normalised value.
 */
export const phoneInputSchema = z.object({
    number: z.string().trim().min(4).max(32),
    /** ISO 3166-1 alpha-2. Nigeria-first, but never Nigeria-only. */
    region: z
        .string()
        .trim()
        .toUpperCase()
        .length(2)
        .regex(/^[A-Z]{2}$/)
        .default('NG'),
});
export const displayNameSchema = z.string().trim().min(2).max(120);
// --- Identity ---------------------------------------------------------------
export const registerRequestSchema = z.object({
    email: emailSchema,
    password: passwordSchema,
    displayName: displayNameSchema,
    phone: phoneInputSchema.optional(),
});
export const loginRequestSchema = z.object({
    email: emailSchema,
    password: z.string().min(1).max(MAX_PASSWORD_LENGTH),
    /**
     * Present on the second leg of a login that required a second factor. Accepts
     * either a 6-digit authenticator code or a recovery code — the server decides
     * which it is, so a locked-out user is not asked to pick the right box first.
     */
    mfaCode: z.string().trim().min(6).max(24).optional(),
    deviceName: z.string().trim().min(1).max(120).optional(),
});
/**
 * Starting a CABS Identity sign-in.
 *
 * `returnTo` is where the browser goes once the sign-in completes. The server
 * checks it against the origins this deployment serves and refuses anything
 * else — an unchecked return target is an open redirect on the far side of an
 * authentication, which is how an authorization code reaches a stranger.
 */
/**
 * Which surface is asking, as a closed set.
 *
 * The browser says which of this deployment's consoles it is; the server maps that to the exact
 * callback registered with CABS Identity. A request never carries a redirect URI, because that is
 * the address an authorization code is delivered to.
 */
export const cabsSurfaceSchema = z.enum(['ATTENDEE', 'ORGANIZER', 'OPS']);
export const cabsStartRequestSchema = z.object({
    returnTo: z.string().url().max(2048),
    /** Omitted by older attendee clients, which only ever meant the attendee surface. */
    surface: cabsSurfaceSchema.default('ATTENDEE'),
});
/**
 * Finishing one.
 *
 * The whole redirect URL is posted back rather than parsed fields, so the
 * server reads `code`, `state` and any `error` from one value it validates
 * itself. Sending pre-extracted fields would move that parsing into the browser,
 * where it is not a security check at all.
 */
export const cabsCallbackRequestSchema = z.object({
    currentUrl: z.string().url().max(4096),
});
/**
 * Starting a migration: linking an existing account to a CABS identity.
 *
 * There is no account identifier in this body, and there must never be one. The
 * account being linked is the one the caller's session already proves; taking it
 * from the request would turn the legacy half of the ceremony from a proof into
 * a claim, which is the whole failure the ceremony exists to prevent.
 */
export const cabsMigrationStartRequestSchema = z.object({
    returnTo: z.string().url().max(2048),
});
/**
 * Finishing a first-use request: creating a new account for a CABS identity.
 *
 * `displayName` is the only thing the person supplies. Everything that decides
 * *which* account this becomes — the subject, the verified address — comes from
 * a validated ID token, never from this body.
 */
export const cabsFirstUseCallbackRequestSchema = z.object({
    currentUrl: z.string().url().max(4096),
    displayName: displayNameSchema,
});
/**
 * A back-channel logout delivered by CABS Identity.
 *
 * One field, and it is a token: everything this endpoint acts on is inside it
 * and is established by the SDK's validation. Nothing the caller states outside
 * the token is trusted, because a caller who could name a session id could end
 * anybody's session.
 */
export const cabsBackchannelLogoutRequestSchema = z.object({
    logout_token: z.string().min(1).max(8192),
});
export const refreshRequestSchema = z.object({
    sessionToken: z.string().min(1).max(512),
});
export const sessionResponseSchema = z.object({
    accessToken: z.string(),
    accessTokenExpiresAt: z.string(),
    /** Opaque. Returned once, stored hashed, never reconstructable from the row. */
    sessionToken: z.string(),
    sessionExpiresAt: z.string(),
    mfaSatisfied: z.boolean(),
});
export const viewerSchema = z.object({
    userId: z.string().uuid(),
    displayName: z.string(),
    email: z.string(),
    emailVerified: z.boolean(),
    phone: z.string().nullable(),
    phoneVerified: z.boolean(),
    createdAt: z.string(),
    mfa: z.object({
        enrolled: z.boolean(),
        /** Whether *this session* has satisfied the second factor. */
        satisfiedForSession: z.boolean(),
    }),
    organizations: z.array(z.object({
        organizationId: z.string().uuid(),
        name: z.string(),
        slug: z.string(),
        membershipStatus: z.enum(['INVITED', 'ACTIVE', 'SUSPENDED', 'REMOVED']),
        roles: z.array(z.enum(ORG_ROLES)),
        capabilities: z.array(z.string()),
        verificationState: z.enum(['UNVERIFIED', 'SUBMITTED', 'IN_REVIEW', 'VERIFIED', 'REJECTED']),
    })),
    platformCapabilities: z.array(z.enum(PLATFORM_CAPABILITIES)),
});
export const mfaEnrolStartResponseSchema = z.object({
    /** Base32 TOTP secret. Shown once, at enrolment, and never returned again. */
    secret: z.string(),
    otpauthUri: z.string(),
    issuer: z.string(),
    accountName: z.string(),
});
export const mfaEnrolConfirmRequestSchema = z.object({
    code: z
        .string()
        .trim()
        .regex(/^\d{6}$/),
});
export const mfaEnrolConfirmResponseSchema = z.object({
    enrolled: z.literal(true),
    /**
     * Single-use recovery codes, returned exactly once. Stored hashed, so a
     * database read cannot recover them and neither can we.
     */
    recoveryCodes: z.array(z.string()),
});
// --- Organizations ----------------------------------------------------------
export const organizationSlugSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(3)
    .max(48)
    .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'Use lowercase letters, numbers and hyphens.');
export const createOrganizationRequestSchema = z.object({
    name: z.string().trim().min(2).max(160),
    slug: organizationSlugSchema,
    /** Country of operation. Drives policy isolation later (SDAD Section 4.10). */
    countryCode: z
        .string()
        .trim()
        .toUpperCase()
        .length(2)
        .regex(/^[A-Z]{2}$/)
        .default('NG'),
    contactEmail: emailSchema,
});
export const updateOrganizationProfileRequestSchema = z.object({
    legalName: z.string().trim().min(2).max(200).nullable(),
    publicName: z.string().trim().min(2).max(160),
    supportEmail: emailSchema.nullable(),
    supportPhone: phoneInputSchema.nullable(),
    websiteUrl: z.string().trim().url().max(500).nullable(),
    description: z.string().trim().max(2000).nullable(),
});
export const organizationVerificationStateSchema = z.enum([
    'UNVERIFIED',
    'SUBMITTED',
    'IN_REVIEW',
    'VERIFIED',
    'REJECTED',
]);
export const organizationSchema = z.object({
    organizationId: z.string().uuid(),
    name: z.string(),
    slug: z.string(),
    countryCode: z.string(),
    contactEmail: z.string(),
    createdAt: z.string(),
    verification: z.object({
        state: organizationVerificationStateSchema,
        submittedAt: z.string().nullable(),
        decidedAt: z.string().nullable(),
        reasonCode: z.string().nullable(),
        reviewerNote: z.string().nullable(),
    }),
    profile: z.object({
        legalName: z.string().nullable(),
        publicName: z.string(),
        supportEmail: z.string().nullable(),
        supportPhone: z.string().nullable(),
        websiteUrl: z.string().nullable(),
        description: z.string().nullable(),
    }),
});
/**
 * The onboarding reviewer's view of an organization.
 *
 * A superset of `Organization` carrying the submitted business-identity
 * evidence. Separate from the organizer-facing shape on purpose: this is Class D
 * data (SDAD Section 40), and only a caller holding `OPS_ORGANIZER_ONBOARDING`
 * ever receives it.
 */
export const opsOrganizationSchema = organizationSchema.extend({
    evidence: z.object({
        legalName: z.string().nullable(),
        registrationNumber: z.string().nullable(),
        representativeName: z.string().nullable(),
        representativeEmail: z.string().nullable(),
        representativePhone: z.string().nullable(),
    }),
});
export const membershipSchema = z.object({
    membershipId: z.string().uuid(),
    userId: z.string().uuid().nullable(),
    displayName: z.string().nullable(),
    email: z.string(),
    status: z.enum(['INVITED', 'ACTIVE', 'SUSPENDED', 'REMOVED']),
    roles: z.array(z.enum(ORG_ROLES)),
    invitedAt: z.string(),
    activatedAt: z.string().nullable(),
});
export const inviteMemberRequestSchema = z.object({
    email: emailSchema,
    roles: z.array(z.enum(ORG_ROLES)).min(1).max(ORG_ROLES.length),
});
export const acceptInvitationRequestSchema = z.object({
    invitationToken: z.string().min(1).max(512),
});
export const setMemberRolesRequestSchema = z.object({
    roles: z.array(z.enum(ORG_ROLES)).min(1).max(ORG_ROLES.length),
});
export const submitVerificationRequestSchema = z.object({
    legalName: z.string().trim().min(2).max(200),
    registrationNumber: z.string().trim().min(2).max(64),
    representativeName: z.string().trim().min(2).max(160),
    representativeEmail: emailSchema,
    representativePhone: phoneInputSchema,
});
export const verificationDecisionRequestSchema = z.object({
    decision: z.enum(['IN_REVIEW', 'VERIFIED', 'REJECTED']),
    reasonCode: z.string().trim().min(2).max(64),
    note: z.string().trim().max(2000).optional(),
});
// --- Audit ------------------------------------------------------------------
export const auditEventSchema = z.object({
    auditEventId: z.string().uuid(),
    occurredAt: z.string(),
    action: z.string(),
    actorUserId: z.string().uuid().nullable(),
    actorDisplayName: z.string().nullable(),
    organizationId: z.string().uuid().nullable(),
    resourceType: z.string(),
    resourceId: z.string().nullable(),
    reasonCode: z.string().nullable(),
    sourceIp: z.string().nullable(),
    metadata: z.record(z.string(), z.unknown()),
});
export const auditQuerySchema = z.object({
    organizationId: z.string().uuid().optional(),
    action: z.string().trim().max(120).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    before: z.string().optional(),
});
// --- Operations -------------------------------------------------------------
export const grantPlatformCapabilityRequestSchema = z.object({
    email: emailSchema,
    capability: z.enum(PLATFORM_CAPABILITIES),
    reasonCode: z.string().trim().min(2).max(64),
});
export const platformGrantSchema = z.object({
    grantId: z.string().uuid(),
    userId: z.string().uuid(),
    email: z.string(),
    displayName: z.string(),
    capability: z.enum(PLATFORM_CAPABILITIES),
    grantedAt: z.string(),
    grantedByUserId: z.string().uuid().nullable(),
    revokedAt: z.string().nullable(),
});
// --- Public / attendee ------------------------------------------------------
/**
 * The attendee surface's honest picture of the platform.
 *
 * Each section reports whether the capability *exists* separately from whether
 * it currently holds anything. Discovery became `AVAILABLE` in Slice 2; tickets
 * stay `NOT_YET_AVAILABLE` until issuance exists in Slice 4, because an empty
 * list would otherwise read as "you have none" rather than "nobody can have
 * one yet".
 */
export const attendeeHomeSchema = z.object({
    discovery: z.object({
        /** `AVAILABLE` since Slice 2: discovery exists, even when nothing is listed. */
        state: z.enum(['NOT_YET_AVAILABLE', 'AVAILABLE']),
        events: z.array(z.unknown()),
    }),
    tickets: z.object({
        /**
         * `AVAILABLE` since Slice 4: ticket ownership exists, so an empty list
         * truthfully means "you have none" rather than "this does not work yet".
         */
        state: z.enum(['NOT_YET_AVAILABLE', 'AVAILABLE']),
        tickets: z.array(z.unknown()),
    }),
});
export const healthSchema = z.object({
    status: z.enum(['ok', 'degraded']),
    release: z.string(),
    checks: z.record(z.string(), z.enum(['ok', 'failed'])),
});
//# sourceMappingURL=schemas.js.map